import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { Marquee } from "../components/Marquee";
import { ProjectViewer } from "../components/work/ProjectViewer";
import { ScrollTimecode } from "../components/ScrollTimecode";
import { SiteFooter } from "../components/SiteFooter";
import { SiteNavigation } from "../components/SiteNavigation";
import { WorkCard } from "../components/work/WorkCard";
import { DesignCard } from "../components/work/DesignCard";
import { useScrollIdle } from "../hooks/useScrollIdle";
import { archiveCategories } from "../data/archive";
import { designProjects, figmaFile } from "../data/design";
import { workApproach } from "../data/portfolio";
import { media, toolTicker } from "../data/media";
import { cardHeading, stageHeading } from "../utils/typography";
import type { WorkItem } from "../types/work";
export function VideosPage() {
  const [viewer, setViewer] = useState<WorkItem | null>(null);
  const [activeTab, setActiveTab] = useState(archiveCategories[0].id);
  const scrolling = useScrollIdle();
  const category = archiveCategories.find(entry => entry.id === activeTab) ?? archiveCategories[0];
  const isDesign = category.id === "product";
  const total = useMemo(() => archiveCategories.reduce((count, entry) => count + entry.items.length, 0) + designProjects.length, []);
  return <div id="top" className="min-h-screen w-full [overflow-x:clip] bg-[#050808] text-white selection:bg-[#54f6c6] selection:text-[#05100d]" data-id="element-605" data-mp="2vydpcl2zr8cp">
      <ScrollTimecode data-id="element-606" data-mp="2n37g1c4ii85f" />
      <SiteNavigation data-id="element-607" data-mp="2n2oakuw1tc8a" />

      <main data-id="element-608" data-mp="2n1wd1eiw54fd">
        <header className="relative overflow-hidden border-b border-white/10 px-5 pb-16 pt-20 md:px-8 md:pb-20 md:pt-24" data-id="element-609" data-mp="2i55wyr7368ty">
          <img src={media.archivePlate} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-30" data-id="element-610" data-mp="3fks6q9mz92lu" />
          <div aria-hidden="true" className="absolute inset-0 bg-[linear-gradient(to_top,#050808_10%,rgba(5,8,8,0.65))]" data-id="element-611" data-mp="3as8vvkfoclkq" />
          <div aria-hidden="true" className="absolute inset-0 film-grain opacity-[0.18]" data-id="element-612" data-mp="3tseojygjdoqb" />

          <div className="relative mx-auto max-w-[1480px]" data-id="element-613" data-mp="2lvfanqcm6u4">
            <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-614" data-mp="3kwpa69di9umf">
              <Link to="/" data-cursor="Back" className="inline-flex min-h-11 items-center gap-3 font-mono text-[11px] uppercase tracking-[0.16em] text-white/55 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-615" data-mp="3oytwso5jdqzk">
                <ArrowLeft className="h-4 w-4" aria-hidden="true" data-id="element-616" data-mp="320h0mtdgmxlb" /> Back to studio
              </Link>
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#54f6c6]" data-id="element-617" data-mp="360rh4zyvp6ef">
                Archive / {String(total).padStart(3, "0")} projects
              </span>
            </div>

            <div className="mt-12 text-center md:mt-16" data-id="element-618" data-mp="148blc2zl8w2g">
              <h1 className={`overflow-hidden ${stageHeading}`} data-id="element-619" data-mp="2oqducbgchuwn">
                <motion.span className="block" initial={{
                y: "110%"
              }} animate={{
                y: 0
              }} transition={{
                duration: 0.95,
                ease: [0.16, 1, 0.3, 1]
              }} data-id="element-620" data-mp="2kpk59er62a26">
                  All <span className="text-[#54f6c6]" data-id="element-621" data-mp="2yhqjuweo88zi">work</span>
                </motion.span>
              </h1>
              <p className="mx-auto mt-8 max-w-2xl text-[15px] leading-8 text-white/65 md:text-base" data-id="element-622" data-mp="2top4knajy7fe">
                The full archive, grouped by discipline. Every frame plays its original source film.
              </p>
            </div>
          </div>
        </header>

        <div className="border-b border-white/10 py-6" data-id="element-623" data-mp="3hg2w4r0vpyzb">
          <Marquee items={toolTicker} data-id="element-624" data-mp="3hdgn0mycbbez" />
        </div>

        <section aria-label="Archive films" className="px-5 py-16 md:px-8 md:py-24" data-id="element-625" data-mp="3ci1co4zjjxnb">
          <div className="mx-auto max-w-[1480px]" data-id="element-626" data-mp="1n10bp7qot739">
            {/* Channel selector: each discipline reads as its own panel rather than a small pill. */}
            <div role="tablist" aria-label="Work categories" className="grid gap-3 md:grid-cols-3 md:gap-4" data-id="element-627" data-mp="1i533jopkv71k">
              {archiveCategories.map((entry, entryIndex) => {
              const selected = entry.id === category.id;
              const count = entry.id === "product" ? designProjects.length : entry.items.length;
              return <button key={entry.id} type="button" role="tab" id={`tab-${entry.id}`} aria-selected={selected} aria-controls={`panel-${entry.id}`} onClick={() => setActiveTab(entry.id)} data-cursor="Filter" className={`group relative overflow-hidden rounded-2xl border px-6 py-4 text-left transition-colors duration-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6] focus-visible:ring-offset-2 focus-visible:ring-offset-[#050808] md:px-7 md:py-5 ${selected ? "border-[#54f6c6] bg-[#54f6c6] text-[#05100d]" : "border-white/12 bg-white/[0.03] text-white hover:border-white/30 hover:bg-white/[0.06]"}`} data-id="element-628" data-mp="1yftvm5hrhrht" data-mpi={entryIndex}>
                    {selected && <motion.span layoutId="tab-glow" aria-hidden="true" className="pointer-events-none absolute -inset-px rounded-2xl shadow-[0_0_60px_-12px_rgba(84,246,198,0.9)]" transition={{
                  duration: 0.5,
                  ease: [0.16, 1, 0.3, 1]
                }} data-id="element-629" data-mp="lg7xnmt3bz9" />}

                    <span className="relative flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.18em]" data-id="element-630" data-mp="3iwuqu1q4bpql">
                      <span className={selected ? "text-[#05100d]/70" : "text-[#54f6c6]"} data-id="element-631" data-mp="3919f3bv8zb3n">
                        {String(entryIndex + 1).padStart(2, "0")}
                      </span>
                      <span className={selected ? "text-[#05100d]/70" : "text-white/40"} data-id="element-632" data-mp="2udsxn20bmypg">
                        {String(count).padStart(2, "0")} {count === 1 ? "project" : "projects"}
                      </span>
                    </span>

                    <span className="relative mt-4 block text-[clamp(1rem,1.35vw,1.3rem)] font-semibold leading-[1.15] tracking-[-0.035em]" data-id="element-633" data-mp="2lf764hcvg9cj">
                      {entry.label}
                    </span>

                    <span className={`relative mt-2 block font-mono text-[11px] uppercase tracking-[0.14em] ${selected ? "text-[#05100d]/65" : "text-white/45"}`} data-id="element-634" data-mp="3e2tiof42uobj">
                      {entry.caption}
                    </span>

                    <span aria-hidden="true" className={`relative mt-4 block h-px w-full origin-left transition-transform duration-500 ${selected ? "scale-x-100 bg-[#05100d]/25" : "scale-x-0 bg-white/25 group-hover:scale-x-100"}`} data-id="element-635" data-mp="3998jwkuntqba" />
                  </button>;
            })}
            </div>

            <div role="tabpanel" id={`panel-${category.id}`} aria-labelledby={`tab-${category.id}`} tabIndex={-1} className="focus-visible:outline-none" data-id="element-636" data-mp="oqtvryidu4ot">
              <div className="mt-12 flex flex-col gap-3 border-b border-white/10 pb-5 font-mono text-[11px] uppercase tracking-[0.16em] text-white/45 sm:flex-row sm:items-center sm:justify-between" data-id="element-637" data-mp="3s8lfyqqa1kcg">
                <span className="text-[#54f6c6]" data-id="element-638" data-mp="61evi6aitjlh">Now showing / {category.label}</span>
                {isDesign && <a href={figmaFile} target="_blank" rel="noreferrer" data-cursor="Open" className="flex min-h-11 items-center gap-2 rounded-xl border border-white/12 px-4 text-white/70 transition-colors hover:border-[#54f6c6]/60 hover:text-[#54f6c6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-639" data-mp="107kzzgzu6b4">
                    Open the Figma file <ExternalLink className="h-3 w-3" aria-hidden="true" data-id="element-640" data-mp="3fwjdj6vo01r5" />
                  </a>}
              </div>

              <p className="mt-6 max-w-2xl text-[15px] leading-8 text-white/65" data-id="element-641" data-mp="3kpm210ll4l0h">{category.blurb}</p>

              {isDesign ? <div className="mt-12 grid gap-8 sm:grid-cols-2 xl:grid-cols-3" data-id="element-642" data-mp="3um8ild6vopwp">
                  {designProjects.map((project, index) => <motion.div key={project.id} initial={{
                opacity: 0,
                y: 28
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: "-8%"
              }} transition={{
                duration: 0.6,
                delay: Math.min(index * 0.05, 0.25),
                ease: [0.16, 1, 0.3, 1]
              }} data-id="element-643" data-mp="2jzi4819m0q9" data-mpi={index}>
                      <DesignCard project={project} data-id="element-644" data-mp="21qodv39zatlp" />
                    </motion.div>)}
                </div> : (/* Columns keep vertical reels and landscape films in one flowing wall. */
            <div key={category.id} className="mt-12 gap-8 [column-fill:_balance] sm:columns-2 lg:columns-3 2xl:columns-4" data-id="element-645" data-mp="1s039ez7miuuv">
                  {category.items.map((item, index) => <motion.div key={item.id} initial={{
                opacity: 0,
                y: 28
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: "-8%"
              }} transition={{
                duration: 0.6,
                delay: Math.min(index * 0.04, 0.24),
                ease: [0.16, 1, 0.3, 1]
              }} className="mb-8 break-inside-avoid" data-id="element-646" data-mp="2knz1trq5u0lu" data-mpi={index}>
                      <WorkCard item={item} scrolling={scrolling} onOpen={setViewer} fitMedia loopOn="hover" data-id="element-647" data-mp="v9kg7gafutuk" />
                    </motion.div>)}
                </div>)}
            </div>
          </div>
        </section>

        <section aria-labelledby="disciplines-heading" className="border-t border-white/10 px-5 py-16 md:px-8 md:py-24" data-id="element-648" data-mp="3inne16c7rvfn">
          <div className="mx-auto max-w-[1480px]" data-id="element-649" data-mp="9o28sql0c2p">
            <div className="rounded-[20px] border border-white/10 bg-white/[0.03] p-7 md:p-10 lg:p-12" data-id="element-650" data-mp="3rmsdcevlnssv">
              <h2 id="disciplines-heading" className={cardHeading} data-id="element-651" data-mp="a5r8oasguwvg">
                {workApproach.heading.start} <span className="text-[#54f6c6]" data-id="element-652" data-mp="588yyi6mkdme">{workApproach.heading.accent}</span>
              </h2>
              <ul className="mt-8 grid max-w-4xl gap-6" data-id="element-653" data-mp="j65ynngdl05a">
                {workApproach.points.map(point => <li key={point.description} className="flex gap-4 sm:gap-5" data-id="element-654" data-mp="e9fjd2cea8kx">
                    <span aria-hidden="true" className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#54f6c6]" data-id="element-655" data-mp="2kxsk79hi02ud" />
                    <p className="text-[15px] leading-7 text-white/85 md:text-base md:leading-8" data-id="element-656" data-mp="2g0sn3o2mmafe">
                      {point.title && <span className="font-medium text-white" data-id="element-657" data-mp="vhzzibkbezup">{point.title}. </span>}
                      <span className="text-white/70" data-id="element-658" data-mp="10f893nredvkc">{point.description}</span>
                    </p>
                  </li>)}
              </ul>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter data-id="element-659" data-mp="qox0hnwrqy74" />
      <ProjectViewer item={viewer} items={category.items} onClose={() => setViewer(null)} onNavigate={setViewer} data-id="element-660" data-mp="qodo0mpw120h" />
    </div>;
}